/**
 * Coerce a date-ish value to the `YYYY-MM-DD` string the date-only columns want.
 *
 * The API serialises dates as Laravel datetimes — "2026-07-21T00:00:00.000000Z".
 * MySQL rejects that literal for a DATE column ("Incorrect date value"), and
 * Laravel's `date` validation rule happily passes it through because it IS a
 * valid date, so the 500 lands at the insert rather than as a 422.
 *
 * Anything already date-only is returned untouched, and empty values pass
 * through as-is so a blank field stays blank rather than becoming "1970-01-01".
 *
 * Deliberately string-first (no `new Date()`) for the common cases: parsing
 * "2026-07-21" as a Date and reformatting it shifts the day in timezones behind
 * UTC, which is how a payment date becomes the 20th for anyone west of Greenwich.
 */
export const toDateOnly = (value) => {
  if (value === null || value === undefined || value === "") return value;

  if (value instanceof Date) {
    if (Number.isNaN(value.getTime())) return null;
    // Local parts, not toISOString(), for the same timezone reason as above.
    const y = value.getFullYear();
    const m = String(value.getMonth() + 1).padStart(2, "0");
    const d = String(value.getDate()).padStart(2, "0");
    return `${y}-${m}-${d}`;
  }

  const str = String(value).trim();
  if (str === "") return value;

  // "2026-07-21", "2026-07-21T00:00:00.000000Z", "2026-07-21 00:00:00" → date part.
  const iso = str.match(/^(\d{4}-\d{2}-\d{2})(?:[T ].*)?$/);
  if (iso) return iso[1];

  // Not a shape we recognise — hand it back rather than guessing. The caller's
  // field may legitimately hold something looser (see warranty_date, which is
  // normalised server-side by App\Support\WarrantyDate).
  return value;
};
